import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Button } from '@fluxora/ui';

import {
  backingScale,
  backingSizeMatches,
  canvasBackingSize,
  configureSignatureStroke,
  pointInCanvas,
  readSignatureFromCanvas,
} from '../services/signature-image.service';

/**
 * Signature pad — draw-to-sign on a canvas (E-SF17).
 *
 * Promoted from `Customer_Portal/src/components/SignaturePad.tsx`, whose posture
 * is preserved verbatim below. All of the logic it carried inline (backing-store
 * scale, geometry, canvas→PNG) now lives in `services/signature-image.service.ts`
 * per CLAUDE.md, which is also what makes this component portable: promoting it
 * to `@fluxora/ui` for FE-CRM-02 is a file move plus label props, not a rewrite.
 *
 * ── THE TYPED NAME IS NOT A FALLBACK, IT IS THE OPERATIVE MARK ─────────────
 * Under the ESIGN Act what binds a signature is the record around it — who
 * consented, to which document version, at what time, from where — not the shape
 * of the glyph. The name the funnel already captured travels to the service and
 * is what the contract is executed on; the drawing is presented alongside it
 * because customers expect to sign something. Anyone reading this should not
 * assume the image is what makes the contract stand up. (E38 §11 / ADR-0010 D5.6.)
 *
 * ── ⚠️ BUT IT IS NOW REQUIRED ANYWAY — AND THAT IS A KNOWN A11Y REGRESSION ──
 * This paragraph used to read "it is therefore not the only way to sign", and it
 * used to be true: the pad was an addition to the typed name, never a gate in front
 * of it. A drawn mark is now COMPULSORY on the storefront (see
 * `SignGateInput.signatureDrawn`), which is a product decision taken above this
 * layer, not an ESIGN one — the paragraph above still stands and the typed name is
 * still what binds.
 *
 * A canvas cannot be operated by keyboard. So the consequence, stated plainly
 * rather than left to be discovered: a keyboard-only or screen-reader customer can
 * no longer complete this funnel unaided. The status line below therefore says
 * exactly that and points at the assisted route, instead of announcing a bare "no
 * signature drawn" to someone with no way to draw one.
 *
 * ⚠️ DO NOT "FIX" THIS by rendering the typed name in a script face and passing it
 * off as the drawn mark. That fabricates a mark the customer never made, on a
 * document they are executing. The honest accommodation is a customer-ELECTED and
 * RECORDED one, which needs a contract field this repo cannot add alone.
 *
 * ── AN UNUSABLE MARK IS REFUSED OUT LOUD, NOT DROPPED ──────────────────────
 * When a drawing fails the client-side bounds, this reports it and hands `null`
 * upward. It must never be discarded quietly: a customer who believes they signed
 * with a drawing, and got a typed-only contract, was misled — which is the exact
 * failure this epic exists to prevent, one layer up.
 */
export interface SignaturePadProps {
  /** Fires with a PNG data URI, or `null` when the pad is cleared, empty or refused. */
  readonly onChange: (dataUri: string | null) => void;
  /** Shown beneath the pad when the drawn mark was refused. An i18n KEY. */
  readonly errorKey?: string;
  readonly disabled?: boolean;
}

export function SignaturePad({ onChange, errorKey, disabled = false }: SignaturePadProps): JSX.Element {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const dirtyRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  /** Size the backing store to the element, accounting for pixel ratio. */
  const resize = useCallback((): void => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const rect = canvas.getBoundingClientRect();
    const scale = backingScale(typeof window === 'undefined' ? 1 : window.devicePixelRatio);
    const size = canvasBackingSize(rect.width, rect.height, scale);
    // Resizing a canvas CLEARS it, so only touch it when the size actually
    // changed — otherwise a stray resize event wipes a signature mid-stroke.
    if (backingSizeMatches(canvas, size)) return;
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;
    configureSignatureStroke(ctx, scale);
  }, []);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const start = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx === null || ctx === undefined) return;
    // Capture the pointer so a stroke that leaves the canvas still finishes here
    // rather than being abandoned mid-line.
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = pointInCanvas(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawingRef.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx === null || ctx === undefined) return;
    const { x, y } = pointInCanvas(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setHasInk(true);
    }
  };

  const end = (): void => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas === null || !dirtyRef.current) return;
    onChange(readSignatureFromCanvas(canvas));
  };

  const clear = (): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas !== null && ctx !== null && ctx !== undefined) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    dirtyRef.current = false;
    setHasInk(false);
    onChange(null);
  };

  return (
    <section className="flex flex-col gap-2" aria-labelledby="sign-signature-heading">
      <div className="flex items-center justify-between gap-2">
        <h2 id="sign-signature-heading" className="text-sm font-medium text-foreground">
          {t('shop.sign.signature.heading')}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={clear}
          disabled={disabled || !hasInk}
        >
          {t('shop.sign.signature.clear')}
        </Button>
      </div>

      {/*
        ── THE PAD HAS TO LOOK LIKE SOMETHING YOU DRAW ON ────────────────────
        It was a 128px-tall rectangle with a 1px border and nothing inside it, at
        the bottom of a 4,263px page. A customer reported, three times, not being
        able to see it — and an empty bordered box beside a dozen other bordered
        boxes gives no reason to try dragging a mouse across it.

        So: a dashed inset well, a ruled signing line with the conventional "✗"
        cue, and a prompt that says what to do. All three are `aria-hidden` and
        `pointer-events-none` — they are DECORATION over the canvas, not content.
        Announcing them would put a second, contradictory description in front of a
        screen-reader user, who cannot use this surface at all and is told so by
        the hint below.

        The prompt disappears the moment there is ink, so it never sits underneath
        a real signature.
      */}
      <div className="relative">
        <canvas
          ref={canvasRef}
          data-testid="signature-pad-canvas"
          className="h-40 w-full cursor-crosshair touch-none rounded-md border-2 border-dashed border-primary/40 bg-background transition-colors hover:border-primary/70 sm:h-44"
          // `img` rather than an interactive role: this surface genuinely cannot be
          // operated without a pointer, and announcing it as a control a screen-reader
          // user cannot use would be a worse lie than describing what it is.
          //
          // ⚠️ It is tempting, now that the mark is required, to promote this to
          // `role="application"` or give it a tabindex so it at least LOOKS
          // reachable. Do not. Focusability without operability is the worse
          // outcome: it puts a stop in the tab order that does nothing. The
          // described route stays the status line below.
          role="img"
          aria-labelledby="sign-signature-heading"
          aria-describedby="sign-signature-hint sign-signature-status"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-4 bottom-6 flex items-end gap-2"
        >
          <span className="text-lg leading-none text-foreground/40">&#10005;</span>
          <span className="h-px flex-1 bg-foreground/25" />
        </div>
        {hasInk ? null : (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-foreground/40"
          >
            {t('shop.sign.signature.placeholder')}
          </span>
        )}
      </div>

      {/* Said plainly rather than hidden in terms: the drawing is required, HOW to
          produce one, and — still — that it is not the thing that legally binds
          them. All three, because a requirement stated without the last part
          implies the drawing is what makes the contract, which is not true. */}
      <p id="sign-signature-hint" className="text-xs text-foreground/60">
        {t('shop.sign.signature.hint')}
      </p>

      {/* Announced, because the only other evidence that a mark exists is visual. */}
      <p id="sign-signature-status" className="sr-only" role="status">
        {hasInk ? t('shop.sign.signature.status.drawn') : t('shop.sign.signature.status.empty')}
      </p>

      {errorKey !== undefined ? (
        <p className="text-xs text-danger" role="alert">
          {t(errorKey)}
        </p>
      ) : null}
    </section>
  );
}
